<template>
  <div class="sol-page page-body">
    <div class="sol-group-hero">
      <CategoryBanner
        full
        :tone="solutionPageHero.tone"
        :icon="solutionPageHero.icon"
        :eyebrow="solutionPageHero.eyebrow"
        :title="solutionPageHero.title"
        :desc="solutionPageHero.desc"
        :image="solutionPageHero.image"
        :photo-frame="solutionPageHero.photoFrame"
      />
    </div>

    <div class="sol-subnav">
      <div class="sol-subnav__inner">
        <nav class="sol-breadcrumb" aria-label="面包屑">
          <a href="#" class="sol-breadcrumb__link" @click.prevent="goHome">主页</a>
          <span class="sol-breadcrumb__sep">/</span>
          <span class="sol-breadcrumb__current">方案中心</span>
        </nav>
      </div>
    </div>

    <div class="sol-tabs-bar">
      <div class="sol-tabs-bar__inner filter-row">
        <button
          v-for="tab in solutionTabs"
          :key="tab.id"
          type="button"
          class="sol-tabs-bar__tab filter-chip"
          :class="{ 'sol-tabs-bar__tab--active filter-chip--active': activeTab === tab.id }"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- 行业案例 -->
    <section v-if="activeTab === 'cases'" class="sol-content">
      <div class="sol-content__inner">
        <div
          v-for="(row, rowIdx) in industryCaseRows"
          :key="rowIdx"
          class="sol-cases-row"
          :class="`sol-cases-row--${row.columns}`"
        >
          <a
            v-for="item in row.items"
            :key="item.id"
            href="#"
            class="sol-case-card"
            :class="`sol-case-card--${item.cardStyle || 'photo'}`"
            @click.prevent="goProducts(item.categoryId)"
          >
            <template v-if="item.cardStyle === 'product'">
              <div class="sol-case-card__stage" :style="{ background: item.accent }">
                <div class="sol-case-card__spotlight" aria-hidden="true"></div>
                <img :src="item.image" :alt="item.title" class="sol-case-card__product-img" loading="lazy" />
              </div>
            </template>
            <template v-else>
              <img
                :src="item.image"
                :alt="item.title"
                class="sol-case-card__photo"
                :style="{ objectPosition: item.position || 'center center' }"
                loading="lazy"
              />
              <div class="sol-case-card__sheen" aria-hidden="true"></div>
            </template>
            <div class="sol-case-card__mask"></div>
            <div class="sol-case-card__footer">
              <span class="sol-case-card__title">{{ item.title }}</span>
              <span class="sol-case-card__arrow" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                </svg>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- 机器人开发方案 -->
    <section v-else class="sol-content">
      <div class="sol-content__inner">
        <div class="sol-dev-grid">
          <a
            v-for="item in robotDevSolutions"
            :key="item.id"
            href="#"
            class="sol-dev-card"
            :style="{ background: item.accent }"
            @click.prevent="goProducts(item.categoryId)"
          >
            <div
              class="sol-dev-card__glow"
              :style="{ background: `radial-gradient(circle, ${item.glow} 0%, transparent 68%)` }"
              aria-hidden="true"
            ></div>
            <div class="sol-dev-card__floor" aria-hidden="true"></div>
            <div class="sol-dev-card__stage">
              <img :src="item.image" :alt="item.title" class="sol-dev-card__img" loading="lazy" />
            </div>
            <div class="sol-dev-card__footer">
              <span class="sol-dev-card__title">{{ item.title }}</span>
              <span class="sol-dev-card__arrow" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                </svg>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import CategoryBanner from '../components/CategoryBanner.vue'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  solutionTabs,
  industryCaseRows,
  robotDevSolutions,
  solutionPageHero,
  defaultSolutionTab,
} from '../data/solutionsCatalog'

const route = useRoute()
const router = useRouter()
const activeTab = ref(defaultSolutionTab)

function syncTabFromRoute() {
  const tab = route.params.tab
  if (tab === 'cases' || tab === 'dev') {
    activeTab.value = tab
  } else {
    activeTab.value = defaultSolutionTab
  }
}

function switchTab(tabId) {
  if (activeTab.value === tabId) return
  activeTab.value = tabId
  router.replace({ name: 'solutions', params: { tab: tabId } })
}

function goHome() {
  router.push({ name: 'home' })
}

function goProducts(categoryId) {
  router.push({ name: 'products', params: { categoryId } })
}

watch(() => route.params.tab, syncTabFromRoute, { immediate: true })
</script>

<style scoped>
.sol-page {
  min-height: calc(100vh - 72px);
  width: 100%;
  overflow-x: hidden;
}

.sol-group-hero {
  width: 100%;
}

.sol-subnav {
  background: transparent;
  border: none;
}

.sol-subnav__inner {
  max-width: min(1400px, 100%);
  margin: 0 auto;
  padding: 14px clamp(16px, 4vw, 72px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.sol-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.sol-breadcrumb__link {
  color: #888;
  text-decoration: none;
  transition: color 0.2s;
}

.sol-breadcrumb__link:hover {
  color: var(--brand-primary);
}

.sol-breadcrumb__sep {
  color: #ccc;
}

.sol-breadcrumb__current {
  color: #333;
  font-weight: 500;
}

/* Subnav & Tabs */
.sol-tabs-bar {
  padding: 0 0 24px;
  background: transparent;
  border: none;
}

.sol-tabs-bar__inner {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: 0 var(--page-padding-x);
  justify-content: center;
}

.sol-tabs-bar__tab {
  position: relative;
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
}

.sol-tabs-bar__tab--active::after {
  display: none;
}

/* Content */
.sol-content {
  padding: 36px 0 72px;
}

.sol-content__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

/* Industry cases grid */
.sol-cases-row {
  display: grid;
  gap: 14px;
  margin-bottom: 14px;
}

.sol-cases-row--2 {
  grid-template-columns: repeat(2, 1fr);
}

.sol-cases-row--3 {
  grid-template-columns: repeat(3, 1fr);
}

.sol-cases-row:last-child {
  margin-bottom: 0;
}

.sol-case-card {
  position: relative;
  display: block;
  height: 300px;
  overflow: hidden;
  text-decoration: none;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  transition:
    transform 0.45s var(--ease-out-expo),
    box-shadow 0.45s var(--ease-out-expo);
}

.sol-cases-row--3 .sol-case-card {
  height: 230px;
}

.sol-case-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
}

/* Photo style cards */
.sol-case-card__photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s var(--ease-out-expo), filter 0.4s;
  filter: saturate(1.05) contrast(1.04) brightness(0.95);
}

.sol-case-card:hover .sol-case-card__photo {
  transform: scale(1.08);
  filter: saturate(1.12) contrast(1.06) brightness(1);
}

.sol-case-card__sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 40%,
    rgba(255, 255, 255, 0.08) 50%,
    transparent 60%
  );
  transform: translateX(-100%);
  transition: transform 0.8s var(--ease-out-expo);
  pointer-events: none;
  z-index: 2;
}

.sol-case-card:hover .sol-case-card__sheen {
  transform: translateX(100%);
}

/* Product style cards (small grid) */
.sol-case-card--product .sol-case-card__stage {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.sol-case-card__spotlight {
  position: absolute;
  width: 70%;
  height: 70%;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
}

.sol-case-card__product-img {
  position: relative;
  z-index: 1;
  max-width: 72%;
  max-height: 72%;
  object-fit: contain;
  filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.45));
  transition: transform 0.5s var(--ease-out-expo);
}

.sol-case-card--product:hover .sol-case-card__product-img {
  transform: scale(1.08) translateY(-4px);
}

.sol-case-card__mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.05) 0%,
    transparent 35%,
    rgba(0, 0, 0, 0.72) 100%
  );
  pointer-events: none;
  z-index: 1;
}

.sol-case-card__footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sol-case-card__title {
  font-size: 21px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
}

.sol-cases-row--3 .sol-case-card__title {
  font-size: 18px;
}

.sol-case-card__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(4px);
  color: #fff;
  transition: transform 0.3s var(--ease-smooth), background 0.3s;
}

.sol-case-card:hover .sol-case-card__arrow {
  transform: translateX(4px);
  background: rgba(255, 255, 255, 0.22);
}

/* Robot dev cards */
.sol-dev-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.sol-dev-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 340px;
  overflow: hidden;
  text-decoration: none;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  transition:
    transform 0.45s var(--ease-out-expo),
    box-shadow 0.45s var(--ease-out-expo);
}

.sol-dev-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.28);
}

.sol-dev-card__glow {
  position: absolute;
  width: 75%;
  height: 75%;
  top: 8%;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  pointer-events: none;
  transition: opacity 0.4s;
}

.sol-dev-card:hover .sol-dev-card__glow {
  opacity: 1.3;
}

.sol-dev-card__floor {
  position: absolute;
  bottom: 22%;
  left: 50%;
  transform: translateX(-50%);
  width: 55%;
  height: 12px;
  background: radial-gradient(ellipse, rgba(77, 163, 255, 0.25) 0%, transparent 70%);
  filter: blur(8px);
  pointer-events: none;
}

.sol-dev-card__stage {
  position: absolute;
  left: 50%;
  top: 42%;
  transform: translate(-50%, -50%);
  width: min(420px, 74%);
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: transform 0.55s var(--ease-out-expo);
}

.sol-dev-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  transition: transform 0.55s var(--ease-out-expo);
  filter: drop-shadow(0 16px 32px rgba(0, 0, 0, 0.5));
}

.sol-dev-card:hover .sol-dev-card__stage {
  transform: translate(-50%, calc(-50% - 6px)) scale(1.03);
}

.sol-dev-card__footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 22px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.55) 100%);
}

.sol-dev-card__title {
  font-size: 19px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}

.sol-dev-card__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  transition: transform 0.3s var(--ease-smooth), background 0.3s;
}

.sol-dev-card:hover .sol-dev-card__arrow {
  transform: translateX(4px);
  background: rgba(255, 255, 255, 0.2);
}

@media (max-width: 900px) {
  .sol-cases-row--2,
  .sol-cases-row--3,
  .sol-dev-grid {
    grid-template-columns: 1fr;
  }

  .sol-case-card,
  .sol-cases-row--3 .sol-case-card {
    height: 240px;
  }

  .sol-dev-card {
    height: 280px;
  }

  .sol-dev-card__stage {
    width: min(360px, 82%);
    height: 168px;
  }

  .sol-subnav__inner,
  .sol-tabs-bar__inner,
  .sol-content__inner {
    padding-left: 16px;
    padding-right: 16px;
  }

  .sol-tabs-bar__inner {
    gap: 28px;
  }
}
</style>
